import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumFeature } from '../components/PremiumFeature';
import axios from 'axios';
import { 
    ArrowLeft, 
    MessageCircleHeart,
    Heart,
    AlertCircle,
    Sparkles,
    RotateCcw,
    Bookmark,
    ChevronRight,
    Check,
    Feather
} from 'lucide-react';
import { toast } from 'sonner';
import { IconButton, PrimaryButton, SecondaryButton, TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard, SelectionCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';
import { ModuleHeader, SafetyFooter } from '../components/brand/QuickExit';
import { 
    MicroInsight, 
    REFRAME_INSIGHTS, 
    LABELING_INSIGHTS,
    EMOTION_WHEEL_INSIGHTS,
    getRandomInsight 
} from '../components/brand/MicroInsight';
import { PersistentBottomBar, BottomBarSpacer } from '../components/brand/PersistentBottomBar';

const API = process.env.REACT_APP_BACKEND_URL;

const SelfTalkReframes = () => {
    const navigate = useNavigate();
    const { canAccess } = useAuth();
    const { checkAccess } = usePremiumFeature();
    const [step, setStep] = useState('select'); // select, reframe, complete
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [currentReframeIndex, setCurrentReframeIndex] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const [showInsight, setShowInsight] = useState(false);
    const [currentInsight, setCurrentInsight] = useState(null);
    const [reframeCount, setReframeCount] = useState(0);

    // Categories with reframes - enhanced with R.A.I.N.-inspired support (implicit)
    // Each category has: validation (Recognize/Allow), curiosity (Investigate), and reframes with Non-identification
    const categories = {
        messed_up: {
            id: 'messed_up',
            name: 'I messed up',
            icon: AlertCircle,
            description: 'When you\'ve made a mistake and can\'t let it go',
            variant: 'rose',
            // R.A.I.N. - Recognize + Allow
            validation: 'It makes sense to feel ashamed or scared here. Notice the feeling as it rises, peaks, and softens.',
            // R.A.I.N. - Investigate (light)
            curiosity: 'Is there a story your brain is telling you about this mistake?',
            reframes: [
                // Enhanced with Non-identification elements
                'Mistakes are how humans learn. You\'re not broken—you\'re growing.',
                'One moment doesn\'t define you. You\'re so much more than this.',
                'What would you say to a friend who made this same mistake?',
                'You can feel bad about what happened AND still be a good person.',
                'Repair is always possible. It\'s never too late to make things better.',
                'Your worth isn\'t measured by your mistakes. It never was.',
                'Being imperfect is not the same as being unworthy of love.',
                'You noticed something went wrong. That awareness is actually a strength.',
                'This feeling of shame is moving through you. It isn\'t who you are.',
                'The mistake happened. It doesn\'t make you a mistake.'
            ]
        },
        upset_with_me: {
            id: 'upset_with_me',
            name: 'Someone is upset with me',
            icon: Heart,
            description: 'When you feel like you\'ve disappointed someone',
            variant: 'lavender',
            // R.A.I.N. - Recognize + Allow
            validation: 'It makes sense to feel anxious or scared when someone is upset. Let the feeling shift and change.',
            // R.A.I.N. - Investigate (light)
            curiosity: 'What story is your mind telling you about what this means?',
            reframes: [
                'Their feelings are valid, AND so are yours. Both can be true.',
                'You can\'t control how others feel. You can only control your response.',
                'Conflict doesn\'t mean the relationship is over. It means you both care.',
                'It\'s okay to take space before responding. Rushing rarely helps.',
                'You can make mistakes in relationships. Everyone does.',
                'Their upset is about their experience. It\'s not the whole truth about you.',
                'You don\'t have to fix everything right now. Some things need time.',
                'Being misunderstood is painful. Your feelings about this matter too.',
                'This fear of rejection is a feeling, not a fact about your worth.',
                'Their reaction is information, not a verdict on who you are.'
            ]
        },
        too_much: {
            id: 'too_much',
            name: 'I feel like I\'m too much',
            icon: Sparkles,
            description: 'When your intensity or needs feel like a burden',
            variant: 'sage',
            // R.A.I.N. - Recognize + Allow
            validation: 'It\'s okay that this feeling is here. You don\'t have to push it away—just notice how it moves.',
            // R.A.I.N. - Investigate (light)
            curiosity: 'Where did you first learn that having needs was wrong?',
            reframes: [
                'Your intensity is a gift, even when it feels like too much.',
                'The right people will have room for all of you.',
                'Having needs doesn\'t make you needy. It makes you human.',
                'You\'re not too much. You might just be around people who are too little.',
                'Your sensitivity is a superpower. It lets you feel deeply and connect genuinely.',
                'Taking up space is not a crime. You deserve to exist fully.',
                'What if you\'re exactly the right amount for the right people?',
                'Your big feelings mean you care deeply. That\'s not a flaw.',
                'This belief about being "too much" is a thought, not your identity.',
                'You are not your needs. You are someone who has needs—like everyone.'
            ]
        }
    };

    const handleSelectCategory = (category) => {
        setSelectedCategory(category);
        setCurrentReframeIndex(Math.floor(Math.random() * category.reframes.length));
        setStep('reframe');
        setReframeCount(0);
        // Show labeling insight when first entering reframe mode
        const insight = getRandomInsight(LABELING_INSIGHTS);
        if (insight && Math.random() > 0.5) {
            setCurrentInsight(insight);
            setShowInsight(true);
        }
    };

    // Get contextual insight - mix of labeling, emotion wheel, and reframe insights
    const getContextualInsight = () => {
        const insightTypes = [
            { insights: LABELING_INSIGHTS, weight: 0.35 },
            { insights: EMOTION_WHEEL_INSIGHTS, weight: 0.25 },
            { insights: REFRAME_INSIGHTS, weight: 0.40 }
        ];
        const rand = Math.random();
        let cumulative = 0;
        for (const { insights, weight } of insightTypes) {
            cumulative += weight;
            if (rand < cumulative) {
                return getRandomInsight(insights);
            }
        }
        return getRandomInsight(REFRAME_INSIGHTS);
    };

    const handleAnotherOne = () => {
        setIsAnimating(true);
        const newReframeCount = reframeCount + 1;
        setReframeCount(newReframeCount);
        
        setTimeout(() => {
            let newIndex;
            do {
                newIndex = Math.floor(Math.random() * selectedCategory.reframes.length);
            } while (newIndex === currentReframeIndex && selectedCategory.reframes.length > 1);
            setCurrentReframeIndex(newIndex);
            setIsAnimating(false);
            
            // Show contextual insight AFTER a reframe (every 2nd reframe, with some randomness)
            if (newReframeCount >= 1 && newReframeCount % 2 === 0 && Math.random() > 0.4) {
                const insight = getContextualInsight();
                if (insight) {
                    setCurrentInsight(insight);
                    setShowInsight(true);
                }
            } else {
                setShowInsight(false);
            }
        }, 200);
    };

    const handleDismissInsight = () => {
        setShowInsight(false);
    };

    const handleSaveReframe = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            await axios.post(`${API}/api/users/me/favourites`, {
                type: 'reframe',
                content: selectedCategory.reframes[currentReframeIndex],
                category: selectedCategory.name
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Saved to your favourites', {
                description: 'Find this reframe anytime in Saved Favourites.',
                icon: <Bookmark className="w-4 h-4 text-[#7D8E95]" />
            });
        } catch (error) {
            console.error('Failed to save favourite:', error);
                toast.error('Failed to save', {
                    description: 'Please try again.'
                });
            }
    };

    const handleBack = () => {
        setStep('select');
        setSelectedCategory(null);
    };

    const handleDone = () => {
        setStep('complete');
    };

    const handleReturnToRegulate = () => {
        navigate('/regulate');
    };

    const handleReturnHome = () => {
        navigate('/home?tab=regulation');
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Orenda Module Header */}
            <ModuleHeader
                title="Self-Talk Reframes"
                onBack={() => step === 'select' ? navigate('/home?tab=regulation') : handleBack()}
                backDestination="/home?tab=regulation"
                exitDestination="/home?tab=regulation"
            />

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* STEP 1: Select Category */}
                {step === 'select' && (
                    <div className="animate-fade-in">
                        <div className="text-center mb-10">
                            <IconContainer variant="lavender" size="lg" useGradient={true} className="mx-auto mb-5">
                                <MessageCircleHeart className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                What's your inner critic saying?
                            </Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)]">
                                Let's find a gentler way to talk to yourself.
                            </BodyText>
                        </div>

                        <div className="space-y-4">
                            {Object.values(categories).map((category, index) => (
                                <SelectionCard
                                    key={category.id}
                                    className="animate-fade-in"
                                    style={{ animationDelay: `${index * 80}ms` }}
                                    onClick={() => handleSelectCategory(category)}
                                    data-testid={`category-${category.id}`}
                                >
                                    <div className="flex items-center gap-5">
                                        <IconContainer variant={category.variant} size="lg" useGradient={true}>
                                            <category.icon className="w-6 h-6" />
                                        </IconContainer>
                                        <div className="flex-1">
                                            <Heading as="h3" size="text-lg" className="mb-1">
                                                "{category.name}"
                                            </Heading>
                                            <BodyText className="text-sm text-[var(--orenda-text-secondary)]">
                                                {category.description}
                                            </BodyText>
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-[var(--orenda-border)]" />
                                    </div>
                                </SelectionCard>
                            ))}
                        </div>

                        <div className="mt-10 text-center">
                            <SmallText className="text-[var(--orenda-text-muted)]">
                                Your thoughts are not facts. They're just thoughts.
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* STEP 2: Show Reframe */}
                {step === 'reframe' && selectedCategory && (
                    <div className="flex flex-col min-h-[75vh]">
                        {/* Category indicator */}
                        <div className="text-center mb-4">
                            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--orenda-border)]/50">
                                <selectedCategory.icon className="w-4 h-4 text-[var(--orenda-text-muted)]" />
                                <SmallText className="text-[var(--orenda-text-secondary)] font-medium">
                                    "{selectedCategory.name}"
                                </SmallText>
                            </div>
                        </div>

                        {/* R.A.I.N. - Recognize + Allow (validation) */}
                        {currentReframeIndex === 0 && selectedCategory.validation && (
                            <div className="mb-4 p-4 rounded-2xl bg-[var(--orenda-warm-sand)]/30 border border-[var(--orenda-border)] animate-fade-in">
                                <div className="flex items-start gap-3">
                                    <Feather className="w-4 h-4 text-[var(--orenda-slate)] mt-0.5 flex-shrink-0" />
                                    <BodyText className="text-[var(--orenda-text-secondary)] text-sm italic leading-relaxed">
                                        {selectedCategory.validation}
                                    </BodyText>
                                </div>
                            </div>
                        )}

                        {/* Reframe Card */}
                        <div className="flex-1 flex items-center justify-center">
                            <div className="glass-card rounded-2xl p-5"
                                variant="elevated"
                                padding="xl"
                                className={`w-full rounded-3xl transition-all duration-200 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                                key={currentReframeIndex}
                            >
                                <div className="text-center py-8">
                                    <IconContainer variant={selectedCategory.variant} size="lg" useGradient={true} className="mx-auto mb-6">
                                        <Heart className="w-6 h-6" />
                                    </IconContainer>
                                    <BodyText className="text-xl md:text-2xl text-[var(--orenda-text-primary)] leading-relaxed font-medium">
                                        {selectedCategory.reframes[currentReframeIndex]}
                                    </BodyText>
                                </div>
                            </div>
                        </div>

                        {/* ADHD Brain Science Micro-Insight (shown AFTER reframes, optional) */}
                        {showInsight && currentInsight && (
                            <div className="my-4">
                                <MicroInsight 
                                    insight={currentInsight}
                                    variant="default"
                                    onDismiss={handleDismissInsight}
                                />
                            </div>
                        )}

                        {/* R.A.I.N. - Investigate (curiosity, optional) */}
                        {selectedCategory.curiosity && (
                            <div className="my-4 text-center">
                                <button
                                    onClick={() => toast(selectedCategory.curiosity, {
                                        icon: <Feather className="w-4 h-4 text-[var(--orenda-slate)]" />,
                                        duration: 6000,
                                    })}
                                    className="text-sm text-[var(--orenda-text-muted)] hover:text-[var(--orenda-text-secondary)] transition-colors underline underline-offset-4 decoration-dotted"
                                >
                                    Go a little deeper (optional)
                                </button>
                            </div>
                        )}

                        {/* Save Button */}
                        <div className="flex justify-center my-4">
                            <TertiaryButton
                                onClick={handleSaveReframe}
                                className="flex items-center gap-2"
                                data-testid="save-btn"
                            >
                                <Bookmark className="w-4 h-4" />
                                Save this reframe
                                {!canAccess('save_favourites') && (
                                    <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-[#7D8E95]/20 text-[#7D8E95]">
                                        <Sparkles className="w-3 h-3 inline" />
                                    </span>
                                )}
                            </TertiaryButton>
                        </div>

                        <BottomBarSpacer height="extended" />
                    </div>
                )}

                {/* STEP 3: Completion */}
                {step === 'complete' && (
                    <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in">
                        <div className="text-center max-w-sm">
                            <div className="w-20 h-20 rounded-full bg-[#FFBB98]/20 flex items-center justify-center mx-auto mb-6">
                                <Heart className="w-10 h-10 text-[#B87B73]" />
                            </div>
                            <Heading as="h2" size="text-2xl" className="mb-3">Be gentle with yourself</Heading>
                            <BodyText className="text-[var(--orenda-text-secondary)] mb-8">
                                {reframeCount > 1 ? `You explored ${reframeCount} reframes.` : 'Your inner voice matters.'} Take what resonates and leave the rest.
                            </BodyText>
                            <div className="space-y-3">
                                <PrimaryButton 
                                    onClick={handleReturnToRegulate} 
                                    className="w-full" 
                                    size="lg"
                                    data-testid="return-to-regulate-btn"
                                >
                                    Back to Regulation Tools
                                </PrimaryButton>
                                <SecondaryButton 
                                    onClick={handleReturnHome} 
                                    className="w-full" 
                                    size="lg"
                                    data-testid="return-home-btn"
                                >
                                    Return Home
                                </SecondaryButton>
                                <button 
                                    onClick={() => setStep('select')} 
                                    className="text-sm text-[#7D8E95] hover:text-[#7D8E95] transition-colors mt-2"
                                >
                                    Try another category
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {/* Persistent Bottom Bar for Reframe */}
                {step === 'reframe' && selectedCategory && (
                    <PersistentBottomBar>
                        <div className="flex gap-3">
                            <SecondaryButton
                                onClick={handleDone}
                                className="flex-1"
                                size="action"
                                data-testid="done-btn"
                            >
                                <Check className="w-4 h-4 mr-1.5" />
                                Done
                            </SecondaryButton>
                            <PrimaryButton
                                onClick={handleAnotherOne}
                                className="flex-1"
                                size="action"
                                data-testid="another-btn"
                            >
                                <RotateCcw className="w-4 h-4 mr-1.5" />
                                Another one
                            </PrimaryButton>
                        </div>
                    </PersistentBottomBar>
                )}

                {/* Safety Footer */}
                <SafetyFooter
                    destination="/home"
                    exitLabel="Come back later"
                    reassurance=""
                />
            </main>
        </div>
    );
};

export default SelfTalkReframes;
